// MIDAS V3 — Logo (Phase 1 base + Phase 6 streaming/halo extension)
//
// Source: ToDo/MidasChat/Claude_Design_files/machineid/design_handoff_midas_v3/
//         chat-window.jsx (lines 535-578) — `MidasLogo` component.
//
// Phase 1 shipped the static variant.
// Phase 6 adds:
//   • `streaming` prop → an SVG `<animateMotion>` accent dot (r=1.6) that
//     traces a Z-path across the carved M. 1.8s linear infinite. Conceptually
//     a CNC tool head pathing across the letter.
//   • `halo` prop → a soft accent-coloured shape behind the logo, animated
//     via `@keyframes midas-halo` (scale 1↔1.10, opacity 0.18↔0.32, 1.6s
//     ease-in-out infinite — keyframes are defined in midas-v3-tokens.scss).
//
// Reduced-motion behaviour:
//   • The keyframes themselves are wrapped in `@media (prefers-reduced-motion:
//     no-preference)` inside the token sheet, so the named `midas-halo`
//     animation simply doesn't exist when reduce is preferred (graceful CSS
//     fallback).
//   • The token sheet also overrides `animation-duration: 0.001ms` on every
//     descendant of `.midas-v3-root` when reduce is preferred. Belt + braces.
//   • In JS, we additionally check `window.matchMedia('(prefers-reduced-
//     motion: reduce)').matches` and skip rendering the `<animateMotion>`
//     element + halo entirely. This is the cleanest path because SVG
//     `<animateMotion>` is harder to suppress purely via CSS.
//
// Spec: MIDAS_V3_PHASE6_STREAMING_ACTIONS_SPEC.md §1
// Master design: MIDAS_V3_MASTER_DESIGN.md §3 (MidasLogo cell)
//
// No MUI, no @emotion, no `sx`, no `dt` tokens.
import React, { useEffect, useState } from "react";

export interface MidasLogoV3Props {
  /** Pixel size — both width and height. Default 28. */
  size?: number;
  /** Optional class for the wrapping span (useful for layout). */
  className?: string;
  /** Accessible label. Defaults to none (logo is decorative when it sits
   *  beside a visible "Midas" text label). */
  ariaLabel?: string;
  /** When true, render the <animateMotion> CNC-style tool-head dot tracing
   *  the M. Respects prefers-reduced-motion. */
  streaming?: boolean;
  /** When true, render a soft pulsing accent halo behind the logo. Respects
   *  prefers-reduced-motion. */
  halo?: boolean;
}

/**
 * Reactive prefers-reduced-motion check.
 *
 * We can't read the matchMedia value at module load — the user can change it
 * mid-session via DevTools emulation, and components must re-render. So we
 * subscribe in a hook and store the current value in state.
 *
 * Default: assume "no-preference" during SSR / initial render. The first
 * effect tick corrects it. Mismatch is fine because the static fallback
 * (just the logo) is a strict subset of the animated output.
 */
const useReducedMotion = (): boolean => {
  const [reduced, setReduced] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mq.matches);
    // Safari < 14 lacks addEventListener on MediaQueryList; fall back to
    // addListener / removeListener for compatibility.
    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (mq as any).addListener(onChange);
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (mq as any).removeListener(onChange);
    };
  }, []);

  return reduced;
};

const MidasLogoV3: React.FC<MidasLogoV3Props> = ({
  size = 28,
  className,
  ariaLabel,
  streaming = false,
  halo = false,
}) => {
  const reducedMotion = useReducedMotion();
  const cornerRadius = size > 28 ? 8 : 6;

  // Final animation gates — the JS gate is the authoritative check, the CSS
  // gates in the token sheet are belt + braces.
  const showHalo = halo && !reducedMotion;
  const showStreamingDot = streaming && !reducedMotion;

  return (
    <span
      className={className}
      aria-hidden={ariaLabel ? undefined : true}
      role={ariaLabel ? "img" : undefined}
      aria-label={ariaLabel}
      style={{
        width: size,
        height: size,
        position: "relative",
        display: "inline-grid",
        placeItems: "center",
        flexShrink: 0,
      }}
    >
      {/* Halo — soft accent shape behind the logo. Sized 130% so it spills
          past the rounded square. */}
      {showHalo && (
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            top: "-15%",
            left: "-15%",
            width: "130%",
            height: "130%",
            borderRadius: "50%",
            background: "var(--accent)",
            // Initial opacity matches the keyframe's low end so a snapshot
            // before the animation kicks in still looks correct.
            opacity: 0.18,
            // 1.6s ease-in-out infinite — keyframes defined in
            // midas-v3-tokens.scss.
            animation: "midas-halo 1.6s ease-in-out infinite",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
      )}

      <svg
        viewBox="0 0 28 28"
        width={size}
        height={size}
        style={{ display: "block", position: "relative", zIndex: 1 }}
      >
        <rect
          width="28"
          height="28"
          rx={cornerRadius}
          fill="var(--midas-v3-ink)"
        />
        {/* M peaks — stroke is the paper colour so the M is "carved out" of
            the ink-coloured square. */}
        <path
          d="M 6.5 20 L 6.5 8 L 14 16 L 21.5 8 L 21.5 20"
          stroke="var(--midas-v3-bg)"
          strokeWidth="2.3"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* CNC tool-head dot — only when streaming + motion is allowed.
            We trace a Z-path across the M peaks (top-left → top-mid → top-
            right → top-mid → top-left). r=1.6 + accent-coloured. 1.8s
            linear infinite. */}
        {showStreamingDot && (
          <circle r="1.6" fill="var(--midas-v3-accent)">
            <animateMotion
              dur="1.8s"
              repeatCount="indefinite"
              calcMode="linear"
              path="M 6.5 8 L 14 16 L 21.5 8 L 14 16 L 6.5 8"
            />
          </circle>
        )}
      </svg>
    </span>
  );
};

export default MidasLogoV3;
